import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";

import { autenticar, type ResultadoLogin } from "../../lib/auth/login";
import { useSessaoStore } from "../../lib/auth/store";

/*
 * Tela de Login local (FE-1 CP2 — ADR 0026 §Decisão D, contrato de auth).
 *
 * Consome `autenticar` (a função ATÔMICA do CP1): login + bootstrap do /eu, ou
 * rollback total. A tela só decide a MENSAGEM a partir do `ResultadoLogin`
 * discriminado — não conhece status HTTP, token nem o erro cru.
 *
 * ANTI-ENUMERAÇÃO NA UI: a mensagem de "credencial" é uma string FIXA e
 * byte-idêntica — NUNCA interpola o e-mail nem indica se o e-mail existe.
 * Espelha o 401 byte-idêntico do backend e do `fazerLogin` (CP1).
 *
 * Sem `<form>` nativo: o submit é um `onClick` no botão (o ambiente exige
 * handlers — evita o reload de página de um submit de form nativo). O Enter
 * também dispara via `onKeyDown` nos campos, sem navegação de form.
 *
 * NAVEGAÇÃO: só path puro ("/"), via `navigate("/", { replace: true })` —
 * nenhum token/credencial vai para rota/query/state.
 */

/** Mensagem fixa anti-enumeração — NÃO interpola o e-mail. */
const MENSAGEM_CREDENCIAL = "e-mail ou senha inválidos";

/** Mapeia cada falha do `ResultadoLogin` para a mensagem exibida ao usuário. */
function mensagemDeErro(resultado: Extract<ResultadoLogin, { ok: false }>): string {
  switch (resultado.tipo) {
    case "credencial":
      // String FIXA — anti-enumeração (não revela se o e-mail existe).
      return MENSAGEM_CREDENCIAL;
    case "rate_limit":
      return resultado.retryAposSegundos === null
        ? "muitas tentativas, tente novamente em instantes"
        : `muitas tentativas, aguarde ${resultado.retryAposSegundos}s`;
    case "indisponivel":
      return "autenticação indisponível no momento";
    case "bootstrap_falhou":
      return "não foi possível carregar suas permissões";
    case "rede":
      return "falha de conexão. Verifique sua rede";
    case "desconhecido":
      return "ocorreu um erro inesperado";
  }
}

export function Login(): JSX.Element {
  const navegar = useNavigate();
  // Hook REATIVO do zustand: quem já está logado não vê o login.
  const autenticado = useSessaoStore((s) => s.accessToken !== null);

  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(false);

  // Sessão já ativa → redireciona para a raiz (path puro, replace).
  if (autenticado) {
    return <Navigate to="/" replace />;
  }

  async function aoEntrar(): Promise<void> {
    // Guard anti-duplo-submit: ignora cliques enquanto a chamada está pendente.
    if (carregando) {
      return;
    }
    setErro(null);
    setCarregando(true);
    try {
      const resultado = await autenticar(email, senha);
      if (resultado.ok) {
        // Path puro, replace — sem token na rota/query/state.
        navegar("/", { replace: true });
        return;
      }
      setErro(mensagemDeErro(resultado));
    } finally {
      // Reabilita o botão/campos qualquer que seja o desfecho.
      setCarregando(false);
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-off-black p-8 font-corpo text-snow">
      <section className="flex w-full max-w-sm flex-col gap-6 rounded-lg bg-snow p-8 text-off-black">
        <h1 className="font-titulo text-2xl text-off-black">
          Fluxo de Caixa Consolidado
        </h1>

        <label className="flex flex-col gap-1 font-subtitulo text-sm">
          E-mail
          <input
            type="email"
            aria-label="E-mail"
            autoComplete="username"
            value={email}
            disabled={carregando}
            onChange={(evento) => setEmail(evento.target.value)}
            className="rounded-md border border-platinum bg-snow p-2 font-corpo text-base text-off-black"
          />
        </label>

        <label className="flex flex-col gap-1 font-subtitulo text-sm">
          Senha
          <input
            type="password"
            aria-label="Senha"
            autoComplete="current-password"
            value={senha}
            disabled={carregando}
            onChange={(evento) => setSenha(evento.target.value)}
            onKeyDown={(evento) => {
              if (evento.key === "Enter") {
                void aoEntrar();
              }
            }}
            className="rounded-md border border-platinum bg-snow p-2 font-corpo text-base text-off-black"
          />
        </label>

        {erro !== null && (
          <p role="alert" aria-live="assertive" className="font-corpo text-sm text-erro">
            {erro}
          </p>
        )}

        <button
          type="button"
          onClick={() => void aoEntrar()}
          disabled={carregando}
          className="rounded-md bg-flame p-2 font-titulo text-base text-snow disabled:opacity-50"
        >
          {carregando ? "Entrando…" : "Entrar"}
        </button>
      </section>
    </main>
  );
}
